import * as THREE from 'three'

/**
 * 一张程序生成的"蓝调时刻"环境贴图（CubeTexture → PMREM），给背景幕墙和水面做反射/IBL。
 *
 * 为什么要自己画：
 *  · 之前整个场景没有任何 envMap（只有 3 盏平行光 + 环境光 + 半球光，纯 diffuse），
 *    所以玻璃幕墙上没有任何"天光"过渡，看着就是一张贴纸 —— 这是"没质感"的主因。
 *  · three 自带的 RoomEnvironment 是摄影棚白墙，会把夜景洗成灰昼。
 *
 * ⚠️ 为什么是 CubeTexture 而不是 equirect（等距柱状）：
 *   three 的 IBL 采样里 `directionToEquirectUV()` 把 **世界 +Y 当上**（`asin(dir.y)`），
 *   而本项目世界是 **Z-up**（X=东 / Y=北 / Z=上，见 sceneView 里 camera.up.set(0,0,1)）。
 *   照 equirect 画，"天空"会贴到楼的北面、"地面"贴到南面，整幅渐变横过来。
 *   立方体贴图是按"面 + 面内两轴"直接索引方向向量的，只要每个纹素按**我们的** d.z 判上下，
 *   采样结果就与坐标约定无关 —— 所以这里逐纹素画 6 张脸。
 *
 * 亮度定标：envMapIntensity 由调用方给（背景楼 0.55 / 水面 1.0），这里只保证
 * 天顶→地平线→地面是一条单调的带，暖色只在地平线附近偏西南一点，别抢主光。
 */

//  faces 顺序按 three 的 CubeTexture：+X, -X, +Y, -Y, +Z, -Z
//  面内轴按 GL 立方体贴图表（ma=主轴、sa/ta=面内轴）。上下方向由主轴决定，
//  所以就算面内 s/t 符号记反，也只影响暖色斑的方位，不会把天空画到地面去。
const FACE_AXES = [
  (s, t) => [1, 2 * s - 1, 2 * t - 1], // +X 东
  (s, t) => [-1, 1 - 2 * s, 2 * t - 1], // -X 西
  (s, t) => [2 * s - 1, 1, 2 * t - 1], // +Y 北
  (s, t) => [2 * s - 1, -1, 1 - 2 * t], // -Y 南
  (s, t) => [2 * s - 1, 2 * t - 1, 1], // +Z 上（天顶）
  (s, t) => [1 - 2 * s, 2 * t - 1, -1], // -Z 下（地底）
]

// 线性色（PMREM 吃的是线性值）：天顶深蓝 → 地平线青蓝亮带 → 下面暗绿反照
const ZENITH = [0.012, 0.030, 0.058]
const HORIZON = [0.075, 0.140, 0.205]
const GROUND = [0.010, 0.016, 0.014]
// 落日渐斑：方位对齐 sceneLights 的 rimSW（az 208°，0=北 +Y、90=东 +X）
const SUN = [0.42, 0.26, 0.12]
const SUN_AZ = (208 * Math.PI) / 180

function smoothstep(a, b, x) {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

function skyAt(dx, dy, dz) {
  const up = dz / (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) || 1) // 面内方向归一化后的"上"分量
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1
  const z = dz / len
  const horiz = 1 - Math.abs(z) // 越接近水平越亮
  let r = ZENITH[0] + (HORIZON[0] - ZENITH[0]) * smoothstep(0.0, 0.55, horiz)
  let g = ZENITH[1] + (HORIZON[1] - ZENITH[1]) * smoothstep(0.0, 0.55, horiz)
  let b = ZENITH[2] + (HORIZON[2] - ZENITH[2]) * smoothstep(0.0, 0.55, horiz)
  if (z < 0) {
    // 下半球：地面反弹（很暗、偏草绿），别给亮色否则楼底会"发光"
    const k = smoothstep(0, 0.6, -z)
    r += (GROUND[0] - r) * k
    g += (GROUND[1] - g) * k
    b += (GROUND[2] - b) * k
  }
  // 暖色斑只贴着地平线，且偏西南：给幕墙一条"夕阳余晖"的方向感
  const az = Math.atan2(dx / len, dy / len) // 0=北，+90=东
  let d = Math.abs(az - SUN_AZ)
  if (d > Math.PI) d = Math.PI * 2 - d
  const blob = Math.pow(Math.max(0, Math.cos(d)), 6) * Math.pow(horiz, 3) * (up > -0.15 ? 1 : 0.2)
  r += SUN[0] * blob
  g += SUN[1] * blob
  b += SUN[2] * blob
  return [r, g, b]
}

/** 画一张 6 面的立方体贴图（每面 size×size，线性 Float 值转 8bit sRGB） */
function paintCube(size) {
  const faces = []
  for (let f = 0; f < 6; f++) {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const img = ctx.createImageData(size, size)
    const ax = FACE_AXES[f]
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const s = (x + 0.5) / size
        const t = (y + 0.5) / size
        const [dx, dy, dz] = ax(s, t)
        const [r, g, b] = skyAt(dx, dy, dz)
        const i = (y * size + x) * 4
        // 立方体贴图作为 envMap 时 three 也按 sRGB 解码，所以这里要把线性值编码回 sRGB
        img.data[i] = toByte(r)
        img.data[i + 1] = toByte(g)
        img.data[i + 2] = toByte(b)
        img.data[i + 3] = 255
      }
    }
    ctx.putImageData(img, 0, 0)
    faces.push(canvas)
  }
  return faces
}

function toByte(linear) {
  const v = Math.min(1, Math.max(0, linear))
  const s = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055
  return Math.round(s * 255)
}

/**
 * @param {THREE.WebGLRenderer} renderer
 * @returns {{texture: THREE.Texture, dispose: Function}|null} PMREM 后的 cubeUV 贴图
 */
export function createDuskEnvironment(renderer) {
  if (!renderer) return null
  const cube = new THREE.CubeTexture(paintCube(64))
  cube.colorSpace = THREE.SRGBColorSpace
  cube.generateMipmaps = false
  cube.minFilter = THREE.LinearFilter
  cube.magFilter = THREE.LinearFilter
  cube.needsUpdate = true
  const pmrem = new THREE.PMREMGenerator(renderer)
  pmrem.compileCubemapShader()
  const rt = pmrem.fromCubemap(cube)
  cube.dispose()
  pmrem.dispose()
  return {
    texture: rt.texture,
    dispose() {
      rt.dispose()
    },
  }
}
